// ============================================================
// PRTCL Reminder Scheduling Engine - Protocol v2
// - Per-component reminders (one row per component)
// - Snapshot-plus-drift-check fire logic
// - Phase gate interlock (gate fires instead of dose reminder
//   when isReadyToAdvance)
// ============================================================

import {
  isNative,
  fireNativeNotification,
  checkNativeNotificationPermission,
  requestNativeNotificationPermission,
  scheduleNativeNotifications,
  cancelNativeNotifications,
  listPendingNativeNotifications,
  NOTIFICATION_ACTION_TYPE_DOSE,
} from './native';
import { uuid } from './uuid';
import { FrequencyPattern } from '../data/types';
import type {
  Protocol,
  ProtocolComponent,
  ProtocolPhase,
  Reminder,
  DoseLog,
} from '../data/types';
import {
  getCurrentPhase,
  getEffectiveSchedule,
  getPhaseProgress,
  type EffectiveSchedule,
} from './protocol';
import { parseLocalDate, isComponentDueOnDate, formatDateStr } from './schedule';
import {
  calculateUnitsToFill,
  formatUnits,
  groupComponents,
  unitsForDose,
  type ComponentGroup,
} from './calculations';
import {
  fetchProfile,
  updateProfile,
  fetchReminders,
  fetchEnabledReminders,
  fetchActiveProtocols,
  fetchProtocols,
  fetchDoseLogs,
  addReminder,
  updateReminder,
  subscribe as subscribeToTable,
} from '../data/cloud';
import { supabase } from '../lib/supabase';

const CHECK_INTERVAL_MS = 60_000;
const TIME_TOLERANCE_MINUTES = 1;
const NOTIFIED_DEDUPE_WINDOW_MS = 5 * 60 * 1000;

let intervalId: ReturnType<typeof setInterval> | null = null;

// ────────────────────────────────────────────────────────────
// Snapshot builder (pure helper, exported for tests)
// ────────────────────────────────────────────────────────────

export function buildReminderSnapshot(
  protocol: Protocol,
  component: ProtocolComponent,
): Omit<Reminder, 'id' | 'protocolId' | 'componentId' | 'peptideName' | 'isEnabled' | 'createdAt' | 'lastNotified'> {
  const phase = getCurrentPhase(component);
  const schedule = getEffectiveSchedule(component, phase);
  // Compute syringe units from the component's reconstitution params.
  // calculateUnitsToFill returns 0 when any input is missing, so we
  // store undefined in that case and the notification falls back to
  // dose-only display.
  const computedUnits = calculateUnitsToFill(
    phase.doseMcg,
    component.vialSizeMg,
    component.bacWaterMl,
    component.syringeType,
  );
  return {
    phaseId: phase.id,
    phaseLabel: phase.label,
    phaseNumber: phase.phaseNumber,
    totalPhases: component.phases.length,
    doseMcg: phase.doseMcg,
    doseUnitDisplay: formatDoseDisplay(phase.doseMcg),
    units: computedUnits > 0 ? computedUnits : undefined,
    frequency: schedule.frequency,
    selectedDays: schedule.selectedDays,
    timesOfDay: schedule.timesOfDay,
    summarySnippet: protocol.plainEnglishSummary?.slice(0, 200),
    lastDriftCheckedAt: new Date().toISOString(),
    snapshotVersion: 0,
  };
}

/**
 * Snapshot for a combined group (v3, E9): ONE reminder per group. The
 * group's FIRST component is the lead - its schedule drives dueness
 * (the wizard asks the schedule once per mixture). doseMcg is the
 * TOTAL mcg in one draw; units is the shared drawUnits, so the
 * notification reads "draw to 20 units" for the whole mixture.
 */
export function buildGroupReminderSnapshot(
  protocol: Protocol,
  group: ComponentGroup,
): Omit<Reminder, 'id' | 'protocolId' | 'componentId' | 'peptideName' | 'isEnabled' | 'createdAt' | 'lastNotified'> {
  const lead = group.components[0];
  const phase = getCurrentPhase(lead);
  const schedule = getEffectiveSchedule(lead, phase);
  const totalMcg = group.components.reduce(
    (sum, c) => sum + getCurrentPhase(c).doseMcg,
    0,
  );
  const units = lead.drawUnits ?? unitsForDose(totalMcg, lead.concentrationMcgPerUnit);
  return {
    phaseId: phase.id,
    phaseLabel: phase.label,
    phaseNumber: phase.phaseNumber,
    totalPhases: lead.phases.length,
    doseMcg: totalMcg,
    doseUnitDisplay: formatDoseDisplay(totalMcg),
    units: units > 0 ? units : undefined,
    frequency: schedule.frequency,
    selectedDays: schedule.selectedDays,
    timesOfDay: schedule.timesOfDay,
    summarySnippet: protocol.plainEnglishSummary?.slice(0, 200),
    lastDriftCheckedAt: new Date().toISOString(),
    snapshotVersion: 0,
    mixGroupId: group.mixGroupId,
  };
}

/**
 * Format a syringe unit count for a notification body. Uses the SAME
 * 0.5-unit snap as every in-app surface (formatUnits): this used to
 * round to 2 decimals, so a notification could say "2.3 units" while
 * the dashboard said "Draw to 2.5 units" for the same dose - two
 * different syringe fills for one instruction. Insulin syringes are
 * only readable to half-unit marks, so the snap is the canonical
 * display (product decision 2026-07-03). Singular form for exactly 1.
 */
function formatUnitsForNotification(units: number): string {
  const display = formatUnits(units);
  return `${display} ${display === '1' ? 'unit' : 'units'}`;
}

export function formatDoseDisplay(doseMcg: number): string {
  if (doseMcg >= 1000) {
    const mg = doseMcg / 1000;
    return `${mg % 1 === 0 ? mg.toFixed(0) : mg.toFixed(1)}mg`;
  }
  return `${doseMcg}mcg`;
}

// ────────────────────────────────────────────────────────────
// Reminder creation - per-component
// ────────────────────────────────────────────────────────────

/** One administration's reminder row: a combined group gets ONE reminder
 *  anchored on its lead component; singles get one per component (v3, E9).
 *  Shared by the raw creator and the idempotent ensure path. */
function buildReminderForGroup(protocol: Protocol, group: ComponentGroup): Reminder {
  const lead = group.components[0];
  const snapshot = group.isCombined
    ? buildGroupReminderSnapshot(protocol, group)
    : buildReminderSnapshot(protocol, lead);
  return {
    id: uuid(),
    protocolId: protocol.id,
    componentId: lead.id,
    peptideName: group.isCombined ? group.displayName : lead.peptideName,
    isEnabled: true,
    createdAt: new Date().toISOString(),
    ...snapshot,
  };
}

/** Raw creator: inserts a fresh row per administration on EVERY call - only
 *  safe exactly once per protocol. App flows should call
 *  ensureRemindersForProtocol instead (idempotent). */
export async function createRemindersForProtocol(protocol: Protocol): Promise<Reminder[]> {
  const created: Reminder[] = [];
  for (const group of groupComponents(protocol)) {
    const reminder = buildReminderForGroup(protocol, group);
    await addReminder(reminder);
    created.push(reminder);
  }
  return created;
}

/**
 * Idempotent reminder creation - THE entry point for every flow (wizard
 * save, scheduled-protocol activation, future resume-from-pause). Unlike
 * the raw creator this:
 *   - re-enables (and snapshot-refreshes) rows the web self-disable branch
 *     turned off while the protocol was dormant,
 *   - creates rows only for administrations that have none,
 *   - never duplicates.
 * Safe to call repeatedly - a retried save or a two-device activation race
 * converges instead of double-notifying (eng review 3A).
 */
export async function ensureRemindersForProtocol(protocol: Protocol): Promise<void> {
  const existing = (await fetchReminders()).filter((r) => r.protocolId === protocol.id);
  const byComponent = new Map(existing.map((r) => [r.componentId, r]));

  for (const group of groupComponents(protocol)) {
    const lead = group.components[0];
    const current = byComponent.get(lead.id);
    if (!current) {
      await addReminder(buildReminderForGroup(protocol, group));
      continue;
    }
    if (!current.isEnabled) {
      // Re-enable with a FRESH snapshot - the schedule may have changed
      // while the row sat disabled.
      const snapshot = group.isCombined
        ? buildGroupReminderSnapshot(protocol, group)
        : buildReminderSnapshot(protocol, lead);
      await updateReminder(current.id, {
        ...snapshot,
        isEnabled: true,
        snapshotVersion: current.snapshotVersion + 1,
      });
    }
    // Enabled + present: leave it alone - the drift check in
    // processReminder/sync owns snapshot freshness for live rows.
  }
}

// ────────────────────────────────────────────────────────────
// Fire-time check
// ────────────────────────────────────────────────────────────

function isDueTimeInWindow(
  timesOfDay: string[],
  now: Date,
  lastNotified: string | undefined,
): string | null {
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  for (const time of timesOfDay) {
    const [hStr, mStr] = time.split(':');
    const targetHour = parseInt(hStr, 10);
    const targetMinute = parseInt(mStr || '0', 10);
    const currentTotal = currentHour * 60 + currentMinute;
    const targetTotal = targetHour * 60 + targetMinute;

    if (Math.abs(currentTotal - targetTotal) <= TIME_TOLERANCE_MINUTES) {
      if (lastNotified) {
        const diffMs = now.getTime() - new Date(lastNotified).getTime();
        if (diffMs < NOTIFIED_DEDUPE_WINDOW_MS) continue;
      }
      return time;
    }
  }
  return null;
}

// Web-polling dueness delegates to the canonical isComponentDueOnDate
// (schedule.ts), the same function the dashboard and the native iOS
// pre-scheduler use. A local isScheduleDueToday copy used to live here;
// it anchored to protocol.startDate and ignored component.startDelay,
// so web reminders fired throughout a delayed component's start-delay
// window and flipped EOD parity / weekly anchor day. One dueness
// function, three surfaces, no drift.

export function isHoldExtensionActive(component: ProtocolComponent, now: Date): boolean {
  if (!component.lastHoldExtendedAt || !component.holdExtensionDays) return false;
  const extendedAt = new Date(component.lastHoldExtendedAt);
  const expiresAt = new Date(extendedAt.getTime() + component.holdExtensionDays * 86_400_000);
  return now < expiresAt;
}

/**
 * Pure helper: given a cached reminder snapshot and live phase/schedule,
 * determine if drift exists. Exported for unit testing.
 */
export function detectDrift(
  reminder: Pick<Reminder, 'phaseId' | 'doseMcg' | 'timesOfDay' | 'frequency' | 'selectedDays'>,
  livePhase: Pick<ProtocolPhase, 'id' | 'doseMcg'>,
  liveSchedule: EffectiveSchedule,
): boolean {
  return (
    livePhase.id !== reminder.phaseId ||
    livePhase.doseMcg !== reminder.doseMcg ||
    !arrayEqual(liveSchedule.timesOfDay, reminder.timesOfDay) ||
    liveSchedule.frequency !== reminder.frequency ||
    !arrayEqual(liveSchedule.selectedDays ?? [], reminder.selectedDays ?? [])
  );
}

/**
 * Process a single reminder: fast-check snapshot, drift-check live,
 * fire gate if phase is ready, otherwise fire dose reminder.
 *
 * TODO(Step 9 - final test pass): Add integration tests for the four
 * dangerous branches in this function:
 *   1. Fast-path hit → fire dose notification (no drift, no gate)
 *   2. Drift recovery → fresh snapshot persisted → re-check dueness
 *   3. Phase gate fire (isReadyToAdvance) → suppresses dose notification
 *   4. Inactive-protocol disable → reminder self-disables on protocol gone
 * Current Step 3 coverage is limited to pure-helper unit tests (detectDrift,
 * isHoldExtensionActive, formatDoseDisplay, buildReminderSnapshot).
 */
async function processReminder(
  reminder: Reminder,
  now: Date,
  protocolsMap: Map<string, Protocol>,
  logsByComponent: Map<string, DoseLog[]>,
): Promise<boolean> {
  if (!reminder.isEnabled) return false;

  // Fast path: would the cached snapshot be due at this minute?
  const snapshotDueTime = isDueTimeInWindow(reminder.timesOfDay, now, reminder.lastNotified);
  if (!snapshotDueTime) return false;

  // Use pre-fetched protocol from batch (no individual query)
  const protocol = protocolsMap.get(reminder.protocolId) ?? null;

  if (!protocol || !protocol.isActive) {
    await updateReminder(reminder.id, { isEnabled: false });
    return false;
  }

  const component = protocol.components.find((c) => c.id === reminder.componentId);
  if (!component) {
    await updateReminder(reminder.id, { isEnabled: false });
    return false;
  }

  // Group reminders (v3): resolve the combined group this reminder
  // stands for. If the group no longer exists (protocol edited), the
  // reminder self-disables like a missing component.
  const group = reminder.mixGroupId
    ? groupComponents(protocol).find((g) => g.mixGroupId === reminder.mixGroupId)
    : undefined;
  if (reminder.mixGroupId && !group) {
    await updateReminder(reminder.id, { isEnabled: false });
    return false;
  }

  const livePhase = getCurrentPhase(component, now);
  const liveSchedule = getEffectiveSchedule(component, livePhase);

  // Group reminders snapshot the TOTAL draw mcg, so drift compares
  // against the live group total, not the lead's per-peptide share.
  const driftPhase = group
    ? {
        id: livePhase.id,
        doseMcg: group.components.reduce((sum, c) => sum + getCurrentPhase(c, now).doseMcg, 0),
      }
    : livePhase;
  const driftDetected = detectDrift(reminder, driftPhase, liveSchedule);

  if (driftDetected) {
    const fresh = group
      ? buildGroupReminderSnapshot(protocol, group)
      : buildReminderSnapshot(protocol, component);
    await updateReminder(reminder.id, {
      ...fresh,
      snapshotVersion: reminder.snapshotVersion + 1,
    });
    reminder = { ...reminder, ...fresh };

    // Re-check dueness against fresh snapshot
    if (!isComponentDueOnDate(component, protocol, now)) return false;
    const refreshedDueTime = isDueTimeInWindow(reminder.timesOfDay, now, reminder.lastNotified);
    if (!refreshedDueTime) return false;
  } else {
    // Also verify day-of-week for frequencies that care about it
    if (!isComponentDueOnDate(component, protocol, now)) return false;
  }

  // Phase gate interlock - use pre-fetched logs from batch (no individual query)
  const componentLogs = logsByComponent.get(component.id) || [];
  const progress = getPhaseProgress(component, livePhase, componentLogs, now);
  const phaseGateReady = progress.isReadyToAdvance && !isHoldExtensionActive(component, now);

  if (phaseGateReady) {
    await firePhaseGateNotification(reminder, component, livePhase);
    await updateReminder(reminder.id, { lastNotified: now.toISOString() });
    return true;
  }

  // Suppress a dose reminder whose slot the user already logged today.
  // Logging a dose EARLY used to still fire at due time, and the tap
  // dead-ended with "not on today's schedule". Completion is per time
  // slot, keyed on the lead component's logs (combined groups write one
  // lead row per administration), the same rule the dashboard uses in
  // generateComponentSchedule. Placed AFTER the phase-gate check so a
  // ready-to-advance gate still fires even when today's dose is logged.
  const dueSlot = isDueTimeInWindow(reminder.timesOfDay, now, undefined);
  const loggedTodayCount = (logsByComponent.get(component.id) ?? []).filter(
    (l) => formatDateStr(new Date(l.loggedTime)) === formatDateStr(now),
  ).length;
  if (dueSlot && reminder.timesOfDay.indexOf(dueSlot) < loggedTodayCount) {
    return false;
  }

  // Compute units fresh from the component at fire time. This avoids
  // a DB schema change (no `units` column needed on the reminders
  // table) and also self-corrects if the user updates their
  // reconstitution params mid-protocol - the next notification will
  // show the new units immediately. Group reminders fire the shared
  // drawUnits - per-peptide vial math is meaningless for a mixture.
  const liveUnits = group
    ? (group.components[0].drawUnits
        ?? unitsForDose(driftPhase.doseMcg, group.components[0].concentrationMcgPerUnit))
    : calculateUnitsToFill(
        livePhase.doseMcg,
        component.vialSizeMg,
        component.bacWaterMl,
        component.syringeType,
      );
  const reminderForFire: Reminder = liveUnits > 0
    ? { ...reminder, units: liveUnits }
    : reminder;

  await fireDoseNotification(reminderForFire);
  await updateReminder(reminder.id, { lastNotified: now.toISOString() });
  return true;
}

function arrayEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ────────────────────────────────────────────────────────────
// Fire loop
// ────────────────────────────────────────────────────────────

export async function checkAndFireReminders(): Promise<number> {
  try {
    // Gate on auth before touching cloud. Without this, unauthed routes
    // (/, /signin, /signup, /terms, /privacy) would flood error logs
    // with "Not authenticated" every 60s while the engine polls.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return 0;

    const profile = await fetchProfile();
    if (!profile?.notificationsEnabled) return 0;
    if (isNative()) {
      const perm = await checkNativeNotificationPermission();
      if (perm !== 'granted') return 0;
    } else {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return 0;
    }

    // Batch-fetch all data upfront: 3 queries total instead of 2N+1
    // (where N = number of reminders). Each processReminder() used to
    // individually fetch its protocol + component dose logs.
    const [reminders, protocols, allLogs] = await Promise.all([
      fetchEnabledReminders(),
      fetchActiveProtocols(),
      fetchDoseLogs(),
    ]);

    // Index protocols by ID for O(1) lookup
    const protocolsMap = new Map<string, Protocol>();
    for (const p of protocols) {
      protocolsMap.set(p.id, p);
    }

    // Index dose logs by componentId for O(1) lookup. Ad-hoc logs
    // (off-protocol, added 2026-04-29) carry null componentId — they
    // don't trigger reminders and shouldn't be bucketed here.
    const logsByComponent = new Map<string, DoseLog[]>();
    for (const log of allLogs) {
      if (log.componentId == null) continue;
      const key = log.componentId;
      if (!logsByComponent.has(key)) logsByComponent.set(key, []);
      logsByComponent.get(key)!.push(log);
    }

    const now = new Date();
    let firedCount = 0;

    for (const reminder of reminders) {
      const fired = await processReminder(reminder, now, protocolsMap, logsByComponent);
      if (fired) firedCount++;
    }
    return firedCount;
  } catch (err) {
    console.error('[PRTCL Reminders] Check failed:', err);
    return 0;
  }
}

// ────────────────────────────────────────────────────────────
// Notification delivery
// ────────────────────────────────────────────────────────────

async function fireDoseNotification(reminder: Reminder): Promise<void> {
  // Terse format - peptide name + units (the actual draw amount on the
  // syringe). Mcg is shown parenthetically as a sanity-check reference
  // but units is what the user actually needs at the moment of dosing.
  // Falls back to dose-only when units snapshot is missing (legacy
  // reminders predating the units field).
  const title = 'Time for your dose';
  const phaseContext =
    reminder.totalPhases > 1
      ? ` · Phase ${reminder.phaseNumber}/${reminder.totalPhases}`
      : '';
  const doseDisplay = reminder.units != null && reminder.units > 0
    ? `${formatUnitsForNotification(reminder.units)} (${reminder.doseUnitDisplay})`
    : reminder.doseUnitDisplay;
  const body = `${reminder.peptideName} - ${doseDisplay}${phaseContext}`;
  const options: NotificationOptions = {
    body,
    icon: '/pwa-192x192.png',
    badge: '/pwa-192x192.png',
    tag: `prtcl-reminder-${reminder.id}`,
    data: {
      type: 'dose_reminder',
      protocolId: reminder.protocolId,
      // Group reminders carry the group's LEAD componentId; action
      // handlers resolve it to the whole administration (E9).
      componentId: reminder.componentId,
      reminderId: reminder.id,
      ...(reminder.mixGroupId ? { mixGroupId: reminder.mixGroupId } : {}),
    },
  };
  await showNotification(title, options);
}

async function firePhaseGateNotification(
  reminder: Reminder,
  component: ProtocolComponent,
  currentPhase: ProtocolPhase,
): Promise<void> {
  const title = `${reminder.peptideName} - Time to check in`;
  const body = `You've completed phase ${currentPhase.phaseNumber}. Tap to review and decide what's next.`;
  const options: NotificationOptions = {
    body,
    icon: '/pwa-192x192.png',
    badge: '/pwa-192x192.png',
    tag: `prtcl-phase-gate-${component.id}`,
    requireInteraction: true,
    data: {
      type: 'phase_gate',
      protocolId: reminder.protocolId,
      componentId: component.id,
      phaseId: currentPhase.id,
    },
  };
  await showNotification(title, options);
}

async function showNotification(title: string, options: NotificationOptions): Promise<void> {
  // Native path: use Capacitor local notifications (fires when app is backgrounded)
  if (isNative()) {
    const data = options.data as Record<string, string> | undefined;
    const id = Math.abs(hashString(options.tag || title));
    // Dose reminders get the action-type id so iOS shows Log Now / Skip
    // when the notification is expanded (long-press or pull-down).
    const actionTypeId = data?.type === 'dose_reminder' ? NOTIFICATION_ACTION_TYPE_DOSE : undefined;
    await fireNativeNotification({ id, title, body: options.body || '', tag: options.tag || '', data, actionTypeId });
    return;
  }

  // Web path: use browser Notification API via service worker
  try {
    const reg = await navigator.serviceWorker?.ready;
    if (reg) {
      await reg.showNotification(title, options);
    } else {
      new Notification(title, options);
    }
  } catch {
    try {
      new Notification(title, options);
    } catch (err) {
      console.error('[PRTCL Reminders] Notification failed:', err);
    }
  }
}

/** Simple string hash to generate numeric notification IDs */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return hash;
}

// ────────────────────────────────────────────────────────────
// Native pre-scheduling (the workhorse for reliable delivery)
//
// Why this exists:
// - The setInterval-based polling below ONLY fires when the app's JS
//   runtime is alive (i.e., app in foreground). When the user locks
//   their phone or backgrounds the app, JS suspends within ~30s and
//   no notifications fire.
// - iOS LocalNotifications, when scheduled with a future `at:` date,
//   are delivered by the OS independently of the app's process. They
//   work even when the app is killed.
// - syncReminderSchedule() is the bridge: it cancels stale schedules
//   and pushes the next 7 days' worth of dose notifications into iOS's
//   scheduling queue. Re-run on app load + after any reminder change.
// ────────────────────────────────────────────────────────────

const SCHEDULE_HORIZON_DAYS = 7;
const PRTCL_DOSE_TAG_PREFIX = 'prtcl-dose';

/**
 * Compute a deterministic 32-bit-ish notification id for a given
 * (reminder, fire date, fire time). Re-running the sync produces the
 * same ids for the same fire slots, so iOS upserts in place rather
 * than creating duplicates.
 */
function computeScheduledNotificationId(reminderId: string, fireAt: Date): number {
  const isoSlot = `${fireAt.getFullYear()}-${fireAt.getMonth()}-${fireAt.getDate()}-${fireAt.getHours()}-${fireAt.getMinutes()}`;
  return Math.abs(hashString(`${PRTCL_DOSE_TAG_PREFIX}-${reminderId}-${isoSlot}`));
}

/**
 * Count doses logged for a component on a given LOCAL calendar date.
 * Mirrors generateComponentSchedule's per-slot completion: the Nth time
 * slot of a day is satisfied once N logs exist for that component on that
 * date. Combined groups write one lead-component row per administration,
 * so counting the lead's logs counts administrations. Ad-hoc logs (null
 * componentId) never match. Exported for tests.
 */
export function countDosesLoggedOnDate(
  logs: DoseLog[],
  componentId: string,
  date: Date,
): number {
  const dateStr = formatDateStr(date);
  let count = 0;
  for (const log of logs) {
    if (
      log.componentId === componentId &&
      formatDateStr(new Date(log.loggedTime)) === dateStr
    ) {
      count++;
    }
  }
  return count;
}

export interface ScheduledDoseNotification {
  id: number;
  title: string;
  body: string;
  at: Date;
  data?: Record<string, string>;
  actionTypeId?: string;
}

/**
 * Pure planner: given the current reminders/protocols/logs, compute every
 * dose notification to pre-schedule across the next SCHEDULE_HORIZON_DAYS.
 * Split out from the native I/O in syncReminderSchedule so the fire-slot
 * logic - including "skip a slot already logged that day" - is unit
 * testable. Exported for tests.
 *
 * The logged-slot skip is the fix for: logging a dose EARLY still left the
 * reminder queued, so it fired at due time and the tap dead-ended with
 * "not on today's schedule". A time slot is skipped when its index is
 * already covered by a logged dose for that component on that date.
 */
export function buildScheduledDoseNotifications(
  reminders: Reminder[],
  protocols: Protocol[],
  logs: DoseLog[],
  now: Date,
): ScheduledDoseNotification[] {
  const protocolMap = new Map(protocols.map((p) => [p.id, p]));
  const notifications: ScheduledDoseNotification[] = [];

  for (const reminder of reminders) {
    const protocol = protocolMap.get(reminder.protocolId);
    if (!protocol || !protocol.isActive) continue;

    const component = protocol.components.find((c) => c.id === reminder.componentId);
    if (!component) continue;

    // Group reminders (v3): resolve the combined group; if it no
    // longer exists (protocol edited), don't pre-schedule - the
    // polling path will self-disable the reminder on next check.
    const group = reminder.mixGroupId
      ? groupComponents(protocol).find((g) => g.mixGroupId === reminder.mixGroupId)
      : undefined;
    if (reminder.mixGroupId && !group) continue;

    for (let dayOffset = 0; dayOffset < SCHEDULE_HORIZON_DAYS; dayOffset++) {
      const targetDate = new Date(now);
      targetDate.setDate(targetDate.getDate() + dayOffset);
      targetDate.setHours(0, 0, 0, 0);

      if (!isComponentDueOnDate(component, protocol, targetDate)) continue;

      // How many of this component's slots are already satisfied by logs
      // on this date. Completion is keyed on the lead component's logs,
      // the same rule generateComponentSchedule uses for the dashboard.
      const loggedCount = countDosesLoggedOnDate(logs, component.id, targetDate);

      const phase = getCurrentPhase(component, targetDate);
      const schedule = getEffectiveSchedule(component, phase);
      const times = schedule.timesOfDay.length > 0 ? schedule.timesOfDay : ['08:00'];

      // Compute units fresh from current reconstitution. Group
      // reminders show the group name + shared drawUnits for the
      // TOTAL mixture draw (per-peptide vial math is meaningless
      // for a mix).
      const groupTotalMcg = group
        ? group.components.reduce((sum, c) => sum + getCurrentPhase(c, targetDate).doseMcg, 0)
        : 0;
      const liveUnits = group
        ? (group.components[0].drawUnits
            ?? unitsForDose(groupTotalMcg, group.components[0].concentrationMcgPerUnit))
        : calculateUnitsToFill(
            phase.doseMcg,
            component.vialSizeMg,
            component.bacWaterMl,
            component.syringeType,
          );
      const doseMcgForDisplay = group ? groupTotalMcg : phase.doseMcg;
      const doseDisplay = liveUnits > 0
        ? `${formatUnitsForNotification(liveUnits)} (${formatDoseDisplay(doseMcgForDisplay)})`
        : formatDoseDisplay(doseMcgForDisplay);
      const displayName = group ? group.displayName : component.peptideName;
      const phaseContext = !group && component.phases.length > 1
        ? ` · Phase ${phase.phaseNumber}/${component.phases.length}`
        : '';

      times.forEach((time, slotIdx) => {
        // Skip a slot the user already logged that day - a reminder here
        // would fire for a done dose and dead-end on tap.
        if (slotIdx < loggedCount) return;

        const [h, m] = time.split(':').map(Number);
        const fireAt = new Date(targetDate);
        fireAt.setHours(h, m, 0, 0);

        // Skip past fire times for today (already missed).
        if (fireAt.getTime() <= now.getTime()) return;

        notifications.push({
          id: computeScheduledNotificationId(reminder.id, fireAt),
          title: 'Time for your dose',
          body: `${displayName} - ${doseDisplay}${phaseContext}`,
          at: fireAt,
          data: {
            type: 'dose_reminder',
            protocolId: protocol.id,
            componentId: component.id,
            reminderId: reminder.id,
            ...(group?.mixGroupId ? { mixGroupId: group.mixGroupId } : {}),
          },
          actionTypeId: NOTIFICATION_ACTION_TYPE_DOSE,
        });
      });
    }
  }

  return notifications;
}

// ────────────────────────────────────────────────────────────
// Scheduled protocols - the "starts today" cue (2026-07-16)
// ────────────────────────────────────────────────────────────

const START_CUE_TAG_PREFIX = 'prtcl-start';

/**
 * Pure planner: one pre-scheduled local notification per SCHEDULED
 * protocol - the start-day cue + backstop for the client-side activation
 * engine (D-Activation, spec 5.5). Fires at the protocol's earliest dose
 * time on its start date (fallback 09:00). Deliberately NOT clamped to
 * the 7-day dose horizon: start dates run up to +90 days and the user may
 * not open the app in between; volume is one slot per scheduled protocol,
 * far under iOS's 64-notification cap. A cue whose moment already passed
 * is skipped - catching up an overdue protocol is the reconcile engine's
 * job, not a stale notification's.
 *
 * Cleanup is free by construction: every resync rebuilds the whole queue,
 * so deleting, pausing, or re-dating a scheduled protocol drops or moves
 * its cue on the next protocols-change resync.
 */
export function buildStartCueNotifications(
  scheduledProtocols: Protocol[],
  now: Date,
): ScheduledDoseNotification[] {
  const notifications: ScheduledDoseNotification[] = [];
  for (const protocol of scheduledProtocols) {
    if (protocol.status !== 'scheduled' || !protocol.startDate) continue;
    const times = protocol.components
      .flatMap((c) => c.timesOfDay)
      .filter(Boolean)
      .sort();
    const [h, m] = (times[0] ?? '09:00').split(':');
    const fireAt = parseLocalDate(protocol.startDate);
    fireAt.setHours(parseInt(h, 10), parseInt(m || '0', 10) || 0, 0, 0);
    if (fireAt.getTime() <= now.getTime()) continue;
    notifications.push({
      id: Math.abs(hashString(`${START_CUE_TAG_PREFIX}-${protocol.id}`)),
      title: 'Your protocol starts today',
      body: `${protocol.name} begins today. Open PRTCL to get going.`,
      at: fireAt,
      data: {
        type: 'protocol_start',
        protocolId: protocol.id,
      },
    });
  }
  return notifications;
}

/**
 * Pre-schedule every dose due in the next SCHEDULE_HORIZON_DAYS days
 * across all active protocols, so iOS delivers them even when the app
 * is killed. Cancels everything PRTCL has scheduled before pushing the
 * fresh batch to keep the slate clean. Also plans one start-day cue per
 * scheduled protocol (buildStartCueNotifications).
 *
 * Doses already logged for a day are skipped (buildScheduledDoseNotifications),
 * so logging a dose early no longer leaves a reminder that fires for a
 * done dose. syncReminderSchedule re-runs on every dose_logs change, so
 * the just-logged slot is dropped from iOS's queue within ~250ms.
 *
 * Safe to call repeatedly. Bails out gracefully on web (no-op) and
 * when notification permission isn't granted (no-op).
 */
export async function syncReminderSchedule(): Promise<number> {
  if (!isNative()) return 0;

  let perm = await checkNativeNotificationPermission();
  if (perm !== 'granted') {
    // Contextual permission ask (spec W5): we only ever ask at the first
    // moment there is something REAL to schedule - which is decided
    // below, after planning. Here we just bail early when the one
    // contextual ask has already been spent (denied users are never
    // nagged by resyncs). The engine starts at app boot (Layout), so
    // asking up here fired the OS sheet on a cold first open with
    // nothing to schedule - the exact anti-pattern W5 removes.
    let asked = false;
    try { asked = localStorage.getItem('prtcl_notif_asked_contextual') === 'true'; } catch {}
    if (asked) return 0;
  }

  // FETCH FIRST, cancel after: the old cancel-then-fetch order meant one
  // offline app-open wiped the whole iOS queue (cancel is local, fetch
  // throws) with nothing re-adding it until the next successful sync -
  // including start cues up to 90 days out (adversarial review A3).
  //
  // ONE protocols read feeds both planners: dose notifications need the
  // active protocols, start cues the scheduled ones. The cue path must NOT
  // sit behind a reminders-exist early return: a first-time scheduled user
  // has ZERO reminder rows (reminders are created at activation) and would
  // otherwise never get a cue (OV1).
  const [reminders, allProtocols] = await Promise.all([
    fetchEnabledReminders(),
    fetchProtocols(),
  ]);

  // Start cues plan FIRST: iOS keeps at most 64 pending notifications and
  // drops the farthest-out ones - exactly the cues - so the dose planner
  // gets whatever budget the cues leave (adversarial review A6).
  const IOS_PENDING_LIMIT = 64;
  const notifications: ScheduledDoseNotification[] = buildStartCueNotifications(
    allProtocols.filter((p) => p.status === 'scheduled'),
    new Date(),
  );

  if (reminders.length > 0) {
    // Dose logs are included so already-logged slots are skipped by the
    // planner (the early-log bug fix).
    const logs = await fetchDoseLogs();
    const doseNotifications = buildScheduledDoseNotifications(
      reminders,
      allProtocols.filter((p) => p.isActive),
      logs,
      new Date(),
    ).sort((a, b) => a.at.getTime() - b.at.getTime());
    notifications.push(
      ...doseNotifications.slice(0, Math.max(0, IOS_PENDING_LIMIT - notifications.length)),
    );
  }

  // The contextual permission ask (spec W5): fires once, at the first
  // sync that actually has notifications to schedule - i.e. right after
  // the user's first protocol with reminders exists. iOS shows the OS
  // sheet at most once per install; the localStorage guard keeps this
  // to a single attempt on our side too.
  if (perm !== 'granted') {
    if (notifications.length === 0) return 0;
    try { localStorage.setItem('prtcl_notif_asked_contextual', 'true'); } catch {}
    const result = await requestNativeNotificationPermission();
    if (result !== 'granted') return 0;
    perm = 'granted';
    void updateProfile({ notificationsEnabled: true });
  }

  // Cancel all currently-pending notifications. We can't filter by
  // tag because iOS doesn't expose tags via getPending(), so we
  // cancel everything PRTCL ever scheduled (which is everything,
  // since this app doesn't use scheduled notifications elsewhere).
  const pending = await listPendingNativeNotifications();
  if (pending.length > 0) {
    await cancelNativeNotifications(pending.map((n) => n.id));
  }

  if (notifications.length > 0) {
    await scheduleNativeNotifications(notifications);
  }
  return notifications.length;
}

// ────────────────────────────────────────────────────────────
// Engine start/stop and permission flow (unchanged from before)
// ────────────────────────────────────────────────────────────

// Engine started flag (separate from intervalId because the polling
// interval is web-only now; native start without it still needs to
// guard against double subscription).
let engineStarted = false;
// Subscriptions set up by startReminderEngine, torn down by stop.
let unsubscribers: Array<() => void> = [];
// Debounce sync calls: many cloud mutations (e.g., creating a protocol
// with 4 components) fire multiple `notify()` calls back-to-back. Coalesce
// them into one sync.
let syncTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleResync(): void {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    syncReminderSchedule().catch((err) => {
      console.error('[reminders] resync failed:', err);
    });
  }, 250);
}

/**
 * Start the reminder engine. As of the iOS pre-scheduling rebuild:
 * - On native: subscribes to protocols/reminders/dose_logs changes and
 *   re-syncs iOS's native scheduling queue every time data changes.
 *   The pre-scheduled notifications fire even when the app is killed,
 *   the phone is locked, or the user is in another app.
 * - On web: keeps the setInterval polling because the browser
 *   Notifications API has no pre-schedule equivalent that survives
 *   tab close. PWAs notify only when the tab is alive.
 */
export function startReminderEngine(): void {
  if (engineStarted) return;
  engineStarted = true;

  if (isNative()) {
    // Initial sync on app load.
    syncReminderSchedule().catch((err) => {
      console.error('[reminders] initial sync failed:', err);
    });
    // Re-sync whenever any of the relevant tables change. Protocols
    // changes can shift dose times. Reminders changes can enable /
    // disable a per-component schedule. Dose-logs changes are
    // included so logging or skipping can clear the imminent
    // notification (iOS already cancels via id reuse on next sync).
    unsubscribers.push(subscribeToTable('protocols', scheduleResync));
    unsubscribers.push(subscribeToTable('reminders', scheduleResync));
    unsubscribers.push(subscribeToTable('dose_logs', scheduleResync));
  }

  // Polling for web only - on native, pre-scheduling owns delivery
  // and running the polling alongside would produce duplicate
  // notifications when both paths think a dose is due in the same
  // minute (the polled fire vs. the pre-scheduled fire).
  if (!isNative()) {
    checkAndFireReminders();
    intervalId = setInterval(checkAndFireReminders, CHECK_INTERVAL_MS);
  }
}

export function stopReminderEngine(): void {
  engineStarted = false;
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  for (const unsub of unsubscribers) unsub();
  unsubscribers = [];
}

export async function requestNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  const result = await requestNativeNotificationPermission();
  if (result === 'granted') {
    await updateProfile({ notificationsEnabled: true });
  }
  return result as NotificationPermission | 'unsupported';
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (isNative()) return 'default'; // async-only on native, use requestNotificationPermission instead
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}
